import { type FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Link,
  OverlayDrawer,
  Text,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  ChatRegular,
  CallRegular,
  Dismiss24Regular,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import ContactSupportForm from '../Layout/ContactSupportForm';
import MokoCard from '../Layout/MokoCard';
import PageHeader from '../Layout/PageHeader';
import { useBreadcrumbs } from '../Layout/useBreadcrumbs';
import PreviousTickets from './PreviousTickets';

const SUPPORT_EMAIL = 'support@quiktrak.com';
const SUPPORT_PHONE = '+1 (555) 000-0000';
const SUPPORT_HOURS = 'Mon-Fri from 8am to 5pm.';

const useStyles = makeStyles({
  header: {
    marginBottom: tokens.spacingVerticalL,
  },
  layout: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingHorizontalXXL),
    '@media (min-width: 1025px)': {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
  },
  formColumn: {
    flexGrow: 1,
    minWidth: 0,
    maxWidth: '640px',
  },
  intro: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXXS),
    marginBottom: tokens.spacingVerticalM,
  },
  introTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  introSubtitle: {
    color: tokens.colorNeutralForeground3,
  },
  infoColumn: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
    width: '100%',
    '@media (min-width: 1025px)': {
      width: '320px',
      flexShrink: 0,
    },
  },
  infoCard: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalM),
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    backgroundColor: tokens.colorNeutralBackground2,
  },
  infoIcon: {
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  infoBody: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalXXS),
  },
  infoTitle: {
    fontWeight: tokens.fontWeightSemibold,
  },
  infoText: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

const ContactSupportPage: FunctionComponent = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const navigate = useNavigate();
  const [showPreviousTickets, setShowPreviousTickets] = useState(false);
  const title = t('contactSupport', 'Contact support');
  useBreadcrumbs([{ label: title }]);

  return (
    <>
      <div className={classes.header}>
        <PageHeader
          onBack={() => navigate(-1)}
          title={title}
          actions={
            <Link onClick={() => setShowPreviousTickets(true)}>
              {t('checkPreviousTickets', 'Check previous tickets')}
            </Link>
          }
        />
      </div>

      <MokoCard>
        <div className={classes.layout}>
          <div className={classes.formColumn}>
            <div className={classes.intro}>
              <Text className={classes.introTitle}>
                {t('contactSupportTitle', 'Any queries? Talk to our team')}
              </Text>
              <Text className={classes.introSubtitle}>
                {t('contactSupportSubtitle', 'Reach out to share your queries')}
              </Text>
            </div>
            <ContactSupportForm
              onBack={() => navigate(-1)}
              backLabel={t('cancel', 'Cancel')}
            />
          </div>

          <div className={classes.infoColumn}>
            <div className={classes.infoCard}>
              <ChatRegular fontSize={24} className={classes.infoIcon} />
              <div className={classes.infoBody}>
                <Text className={classes.infoTitle}>
                  {t('chatToUs', 'Chat to us')}
                </Text>
                <Text className={classes.infoText}>
                  {t('chatToUsSubtitle', 'Our friendly team is here to help.')}
                </Text>
                <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>
              </div>
            </div>

            <div className={classes.infoCard}>
              <CallRegular fontSize={24} className={classes.infoIcon} />
              <div className={classes.infoBody}>
                <Text className={classes.infoTitle}>
                  {t('callUs', 'Call us')}
                </Text>
                <Text className={classes.infoText}>{SUPPORT_HOURS}</Text>
                <Link href={`tel:${SUPPORT_PHONE.replace(/[^\d+]/g, '')}`}>
                  {SUPPORT_PHONE}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </MokoCard>

      <OverlayDrawer
        open={showPreviousTickets}
        onOpenChange={(_, data) => setShowPreviousTickets(data.open)}
        position="end"
        size="medium"
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label={t('close', 'Close')}
                icon={<Dismiss24Regular />}
                onClick={() => setShowPreviousTickets(false)}
              />
            }
          >
            {t('previousTickets', 'Previous tickets')}
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>{showPreviousTickets && <PreviousTickets />}</DrawerBody>
      </OverlayDrawer>
    </>
  );
};

export default ContactSupportPage;
